import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { 
  MessageSquare, Building2, Mail, Phone, Clock, CheckCircle,
  User, Filter, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function InquiriesPage() {
  const { api } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/inquiries');
      setInquiries(response.data);
    } catch (error) {
      toast.error('Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const handleStatusUpdate = async (inquiryId, status) => {
    try {
      await api.put(`/inquiries/${inquiryId}/status?status=${status}`);
      toast.success(`Inquiry status updated to ${status}`);
      fetchInquiries();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-[#D4AF37]/20 text-[#D4AF37]',
      contacted: 'bg-[#4B167A]/40 text-white',
      in_progress: 'bg-[#F59E0B]/20 text-[#F59E0B]',
      closed: 'bg-[#10B981]/20 text-[#10B981]'
    };
    return styles[status] || 'bg-white/10 text-white';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredInquiries = inquiries.filter(i => {
    if (filter === 'all') return true;
    return i.status === filter;
  });

  const newCount = inquiries.filter(i => i.status === 'new').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="inquiries-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Client <span className="text-[#D4AF37]">Inquiries</span>
            </h1>
            <p className="text-white/50 mt-1">
              {newCount > 0 ? `${newCount} new inquiry${newCount > 1 ? 'ies' : ''} pending` : 'All inquiries reviewed'}
            </p>
          </div>
          
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-[#0A091E]/50 border-white/20 text-white" data-testid="select-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1F1B71] border-white/10 text-white">
              <SelectItem value="all">All Inquiries</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Total</p>
                  <p className="text-2xl font-bold text-white">{inquiries.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">New</p>
                  <p className="text-2xl font-bold text-[#D4AF37]">{newCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">In Progress</p>
                  <p className="text-2xl font-bold text-white">
                    {inquiries.filter(i => i.status === 'in_progress').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                </div>
                <div>
                  <p className="text-white/50 text-xs uppercase">Closed</p>
                  <p className="text-2xl font-bold text-white">
                    {inquiries.filter(i => i.status === 'closed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Inquiries List */}
        <Card className="glass-card border-0">
          <CardHeader className="border-b border-white/10">
            <CardTitle className="flex items-center gap-2 text-white">
              <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
              Client Inquiries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-white/50">Loading inquiries...</div>
            ) : filteredInquiries.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">
                  {filter === 'all' ? 'No inquiries yet' : `No ${filter} inquiries`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-white/10">
                {filteredInquiries.map((inquiry) => (
                  <div
                    key={inquiry.id}
                    className={`p-6 hover:bg-white/5 ${inquiry.status === 'new' ? 'bg-[#D4AF37]/5' : ''}`}
                    data-testid={`inquiry-${inquiry.id}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-white">{inquiry.name}</h3>
                          <Badge className={getStatusBadge(inquiry.status)}>
                            {inquiry.status.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-white/50 mb-3">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {inquiry.company}
                          </span>
                          {inquiry.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {inquiry.email}
                            </span>
                          )}
                          {inquiry.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-4 h-4" />
                              {inquiry.phone}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(inquiry.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-white/70 bg-[#0A091E]/50 p-4 rounded-lg">
                          {inquiry.message}
                        </p>
                      </div>

                      <div className="flex flex-row lg:flex-col gap-2">
                        {inquiry.status === 'new' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusUpdate(inquiry.id, 'contacted')}
                            className="bg-[#4B167A] hover:bg-[#4B167A]/80 text-white"
                            data-testid={`btn-contacted-${inquiry.id}`}
                          >
                            Mark Contacted
                          </Button>
                        )}
                        {inquiry.status === 'contacted' && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusUpdate(inquiry.id, 'in_progress')}
                            className="bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-[#0A091E]"
                            data-testid={`btn-progress-${inquiry.id}`}
                          >
                            Start Progress
                          </Button>
                        )}
                        {(inquiry.status === 'in_progress' || inquiry.status === 'contacted') && (
                          <Button
                            size="sm"
                            onClick={() => handleStatusUpdate(inquiry.id, 'closed')}
                            className="bg-[#10B981] hover:bg-[#10B981]/80 text-white"
                            data-testid={`btn-close-${inquiry.id}`}
                          >
                            Close Inquiry
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
